import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {WorkflowStep} from "../workflow-step";
import {DeploymentService} from "../../deployment/deployment.service";
import {SearchHelpers} from "../../helpers/search-helper";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {UsageCollectionsService} from "../../deployment/usage-collection-service";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {DeploymentGqlModel} from "../../deployment/deployment.gql-model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 5;

@Component({
  templateUrl: "./verify-collected-assets.template.pug",
  providers: [
    DeploymentService,
    UsageCollectionsService,
  ],
})

export class VerifyAssetsCollectedComponent extends WorkflowStep {
  deploymentId: string;
  message: string = "";
  error: Error;
  assetsWithoutCollections = [];
  assetsFiltered = [];
  assetsToShow = [];
  deployment: DeploymentGqlModel = new DeploymentGqlModel({});

  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number = 1;
  searchText: string = "";

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    private deploymentService: DeploymentService,
    private usageCollectionsService: UsageCollectionsService,
  ) {
    super();
  }

  @handleErrors()
  async verifyCollectedAssets() {
    const result = await this.usageCollectionsService.getAssets(this.workflowTargetInfo.targetId);

    this.message = result.message;
    this.assetsWithoutCollections = result.assetsWithoutCollections;

    if (result.assetsWithoutCollections.length) return;

    await this.workflowService.setStepStatus(WorkflowStepStatus.InProgress, this.workflowService.currentStep.stepName);
  }

  private loadAssetsToDisplay() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.assetsFiltered = this.assetsWithoutCollections.filter((d) => {
      if (this.searchText) {
        const values = [
          d.serialNumber,
          d.productNumber,
          d.ipAddress || "unset",
          d.hostName || "unset",
          d.k2IpAddress || "unset",
          d.k2HostName || "unset",
        ].filter(it => !!it);

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.assetsToShow = this.assetsFiltered.slice(start, end);

    this.maxPages = Math.ceil(this.assetsFiltered.length / (+this.pageSize));
  }

  async ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.deploymentId = workflowTargetInfo.targetId;
    this.deployment = await this.deploymentService.getDeploymentById(this.deploymentId);
    await this.verifyCollectedAssets();
    this.loadAssetsToDisplay();
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        break;
    }

    this.loadAssetsToDisplay();
  }
}
